import { zodResolver } from '@hookform/resolvers/zod'
import { ChangeEvent } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { FaX } from 'react-icons/fa6'
import { z } from 'zod'

import { Button } from '../../../../components/Button'
import { Input } from '../../../../components/Input'
import { useDialogBox } from '../../../../hooks/useDialogBox'
import { createPromotersCompany, PromotersCompany, updatePromotersCompany } from '../../../../services/promotersCompaniesServices'
import { formatCNPJ } from '../../../../utils/formatCNPJ'

interface ModalUpsertPromotersCompanyProps {
  onClose: () => void
  refetchList: () => void
  promotersCompany?: PromotersCompany | null
}

const upsertPromotersCompanyFormSchema = z.object({
  name: z.string(),
  document: z.string().transform((value) => value.replace(/\D/g, '')),
})

type UpsertPromotersCompanyFormData = z.infer<typeof upsertPromotersCompanyFormSchema>

export function ModalUpsertPromotersCompany({ onClose, refetchList, promotersCompany }: ModalUpsertPromotersCompanyProps) {
  const { showDialog } = useDialogBox()

  const { register, handleSubmit, control } = useForm<UpsertPromotersCompanyFormData>({
    resolver: zodResolver(upsertPromotersCompanyFormSchema),
    defaultValues: {
      name: promotersCompany?.name,
      document: promotersCompany?.document,
    },
  })

  async function handleSubmitUpsertPromotersCompanyForm(data: UpsertPromotersCompanyFormData) {
    let result
    if (promotersCompany) {
      result = await updatePromotersCompany({
        name: data.name,
        document: data.document,
        promotersCompanyId: promotersCompany.id,
      })
    } else {
      result = await createPromotersCompany({
        name: data.name,
        document: data.document,
      })
    }

    if (result) {
      if (result.success) {
        showDialog({
          title: 'Sucesso',
          description: result.message,
          type: 'success',
        })
        refetchList()
        onClose()
      } else {
        showDialog({
          title: 'Erro',
          description: result?.message,
        })
      }
    }
  }

  return (
    <div
      className="fixed top-0 left-0 w-screen h-screen bg-black/65 flex items-center justify-center z-[10000]"
      onClick={onClose}
    >
      <div
        className="flex flex-col bg-slate-100 relative rounded-md p-4 w-[80vw] max-h-[80dvh] md:w-96"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex justify-between mb-4">
          <h1 className="font-semibold text-2xl">
            {promotersCompany
              ? 'Editar'
              : 'Criar'} empresa de promotores
          </h1>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
          >
            <FaX />
          </Button>
        </header>
        <form
          className="flex flex-col space-y-4 w-full"
          onSubmit={handleSubmit(handleSubmitUpsertPromotersCompanyForm)}
        >
          {promotersCompany &&
            <div>
              <Input
                placeholder="Identificador"
                label="ID"
                value={promotersCompany?.id}
                readOnly
                disabled
              />
            </div>}
          <div>
            <Input
              placeholder="Nome"
              label="Nome"
              required
              {...register('name')}
            />
          </div>
          <div>
            <Controller
              name="document"
              control={control}
              render={({ field, fieldState }) => (
                <>
                  <Input
                    {...field}
                    required
                    placeholder="00.000.000/0000-00"
                    label="CNPJ"
                    error={fieldState.error}
                    value={formatCNPJ(field.value || '')}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => field.onChange(formatCNPJ(e.target.value))}
                  />
                </>
              )}
            />
          </div>
          <footer className="ml-auto pt-4">
            <Button
              type="submit"
              size="sm"
            >
              Salvar
            </Button>
          </footer>
        </form>
      </div>
    </div>
  )
}
